import { doorOf } from "../generators.js";
import type { Footprint } from "../footprints.js";
import type { SceneParcel } from "../blueprint/rules.js";

/**
 * The street furniture and ground layers of the city, computed from the parcels and the buildings
 * on them (pure, so it is unit tested; the scene turns the lists into instanced meshes). Units are
 * meters in world coordinates, +X east, +Z south.
 */

export interface Rect {
  x: number;
  z: number;
  /** Full width (X) and depth (Z). */
  w: number;
  d: number;
}
export interface Spot {
  x: number;
  z: number;
  /** Yaw in radians about +Y. */
  yaw: number;
}
export interface Strip {
  /** Centre, direction of travel and size. */
  x: number;
  z: number;
  /** True when the road runs along Z (a north-south street). */
  alongZ: boolean;
  length: number;
  width: number;
}

export interface UrbanLayout {
  /** Paved base of every parcel (the sidewalk), tagged by purpose. */
  pads: (Rect & { public: boolean })[];
  /** Lawns on company parcels. */
  lawns: Rect[];
  /** Paths from a building's door to the street. */
  paths: Rect[];
  hedges: Rect[];
  lamps: Spot[];
  benches: Spot[];
  bushes: { x: number; z: number; r: number }[];
  roads: Strip[];
}

export interface UrbanOptions {
  /** Low tier: fewer props. */
  lean: boolean;
}

const PAD_INSET = 0.5;
const SIDEWALK = 3.2;
const MIN_ROAD_GAP = 6;
const MAX_ROAD_WIDTH = 16;

/** Gaps (free corridors) between merged intervals. */
function gaps(intervals: [number, number][]): [number, number][] {
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  let end = -Infinity;
  for (const [a, b] of sorted) {
    if (end !== -Infinity && a - end >= MIN_ROAD_GAP) out.push([end, a]);
    end = Math.max(end, b);
  }
  return out;
}

export function roadsBetween(parcels: readonly SceneParcel[]): Strip[] {
  if (parcels.length < 2) return [];
  const b = parcels.map((p) => p.bounds);
  const minX = Math.min(...b.map((r) => r.minX));
  const maxX = Math.max(...b.map((r) => r.maxX));
  const minZ = Math.min(...b.map((r) => r.minZ));
  const maxZ = Math.max(...b.map((r) => r.maxZ));
  const out: Strip[] = [];
  const reach = 70;
  for (const [a, c] of gaps(b.map((r) => [r.minX, r.maxX]))) {
    const width = Math.min(MAX_ROAD_WIDTH, c - a);
    out.push({
      x: (a + c) / 2,
      z: (minZ + maxZ) / 2,
      alongZ: true,
      length: maxZ - minZ + 2 * reach,
      width,
    });
  }
  for (const [a, c] of gaps(b.map((r) => [r.minZ, r.maxZ]))) {
    const width = Math.min(MAX_ROAD_WIDTH, c - a);
    out.push({
      x: (minX + maxX) / 2,
      z: (a + c) / 2,
      alongZ: false,
      length: maxX - minX + 2 * reach,
      width,
    });
  }
  return out.slice(0, 96);
}

const GROUND_RAY_EPS = 1e-6;

/** Distance along a ray from inside an axis-aligned box to the box's edge. */
function exitDistance(
  px: number,
  pz: number,
  dx: number,
  dz: number,
  box: { minX: number; maxX: number; minZ: number; maxZ: number },
): number {
  let t = Infinity;
  if (Math.abs(dx) > GROUND_RAY_EPS) t = Math.min(t, ((dx > 0 ? box.maxX : box.minX) - px) / dx);
  if (Math.abs(dz) > GROUND_RAY_EPS) t = Math.min(t, ((dz > 0 ? box.maxZ : box.minZ) - pz) / dz);
  return Number.isFinite(t) ? Math.max(0, t) : 0;
}

export function urbanLayout(
  parcels: readonly SceneParcel[],
  buildings: readonly Footprint[],
  options: UrbanOptions,
): UrbanLayout {
  const layout: UrbanLayout = {
    pads: [],
    lawns: [],
    paths: [],
    hedges: [],
    lamps: [],
    benches: [],
    bushes: [],
    roads: roadsBetween(parcels),
  };
  const yawOf = (nx: number, nz: number) => Math.atan2(nx, nz);
  for (const p of parcels) {
    const { minX, maxX, minZ, maxZ } = p.bounds;
    const w = maxX - minX - 2 * PAD_INSET;
    const d = maxZ - minZ - 2 * PAD_INSET;
    if (w < 6 || d < 6) continue;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const isPublic = p.purpose !== "company";
    layout.pads.push({ x: cx, z: cz, w, d, public: isPublic });
    const hx = w / 2 - 1.3;
    const hz = d / 2 - 1.3;
    // Lamps: corners; middle of the sides too on the high tier.
    const lampSpots: [number, number][] = [
      [-hx, -hz],
      [hx, -hz],
      [hx, hz],
      [-hx, hz],
    ];
    if (!options.lean && w > 30) lampSpots.push([0, -hz], [0, hz]);
    if (!options.lean && d > 30) lampSpots.push([-hx, 0], [hx, 0]);
    for (const [lx, lz] of lampSpots) {
      // The arm points out toward the street, away from the parcel's middle.
      layout.lamps.push({ x: cx + lx, z: cz + lz, yaw: yawOf(lx, lz) });
    }
    if (isPublic) {
      for (const [sx, sz] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ] as const) {
        layout.bushes.push({ x: cx + sx * (hx - 2.2), z: cz + sz * (hz - 2.2), r: 1.5 });
      }
      if (!options.lean) {
        for (const s of [-1, 1]) {
          layout.benches.push({ x: cx + s * (hx - 4), z: cz + (hz - 3.4), yaw: Math.PI });
          layout.benches.push({ x: cx + s * (hx - 4), z: cz - (hz - 3.4), yaw: 0 });
        }
      }
      continue;
    }
    // Company parcel: lawn inside a sidewalk, hedges, a path from the door.
    const lawn: Rect = { x: cx, z: cz, w: w - 2 * SIDEWALK, d: d - 2 * SIDEWALK };
    if (lawn.w < 4 || lawn.d < 4) continue;
    layout.lawns.push(lawn);
    const building = buildings.find(
      (b) => b.kind === "building" && b.x >= minX && b.x <= maxX && b.z >= minZ && b.z <= maxZ,
    );
    let doorSide: "n" | "s" | "e" | "w" | null = null;
    if (building && building.node.geometry.kind === "procedural") {
      const door = doorOf(building.node.geometry.params);
      const cos = Math.cos(building.yaw);
      const sin = Math.sin(building.yaw);
      // Local (x, z) -> world: yaw rotates about +Y.
      const wx = door.position[0] * cos + door.position[1] * sin;
      const wz = -door.position[0] * sin + door.position[1] * cos;
      const nx = door.normal[0] * cos + door.normal[1] * sin;
      const nz = door.normal[0] * -sin + door.normal[1] * cos;
      const px = building.x + wx;
      const pz = building.z + wz;
      const len = exitDistance(px, pz, nx, nz, {
        minX: minX + PAD_INSET,
        maxX: maxX - PAD_INSET,
        minZ: minZ + PAD_INSET,
        maxZ: maxZ - PAD_INSET,
      });
      if (len > 1) {
        const along = Math.abs(nx) > Math.abs(nz);
        layout.paths.push({
          x: px + (nx * len) / 2,
          z: pz + (nz * len) / 2,
          w: along ? len : 2.6,
          d: along ? 2.6 : len,
        });
        doorSide = Math.abs(nx) > Math.abs(nz) ? (nx > 0 ? "e" : "w") : nz > 0 ? "s" : "n";
        if (!options.lean) {
          const lat = along ? [0, 1] : [1, 0];
          for (const s of [-1, 1]) {
            const at = Math.min(len - 1.5, 7);
            layout.benches.push({
              x: px + nx * at + lat[0]! * s * 4.2,
              z: pz + nz * at + lat[1]! * s * 4.2,
              yaw: yawOf(-lat[0]! * s, -lat[1]! * s),
            });
          }
        }
      }
    }
    const t = 0.8;
    const sides: [Rect, "n" | "s" | "e" | "w"][] = [
      [{ x: lawn.x, z: lawn.z - lawn.d / 2 + t / 2, w: lawn.w, d: t }, "n"],
      [{ x: lawn.x, z: lawn.z + lawn.d / 2 - t / 2, w: lawn.w, d: t }, "s"],
      [{ x: lawn.x + lawn.w / 2 - t / 2, z: lawn.z, w: t, d: lawn.d }, "e"],
      [{ x: lawn.x - lawn.w / 2 + t / 2, z: lawn.z, w: t, d: lawn.d }, "w"],
    ];
    for (const [rect, side] of sides) {
      if (side !== doorSide) layout.hedges.push(rect);
    }
  }
  return layout;
}
